import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { initMetaPixel } from "./lib/tracking";
import "./lib/i18n";

// Store Pages
import HomePage from "./pages/store/HomePage";
import LooksPage from "./pages/store/LooksPage";
import LookDetailPage from "./pages/store/LookDetailPage";
import ProductDetailPage from "./pages/store/ProductDetailPage";
import QuizPage from "./pages/store/QuizPage";
import CartPage from "./pages/store/CartPage";
import CheckoutPage from "./pages/store/CheckoutPage";
import OrderConfirmationPage from "./pages/store/OrderConfirmationPage";
import AccountPage from "./pages/store/AccountPage";
import LoginPage from "./pages/store/LoginPage";
import OrdersPage from "./pages/store/OrdersPage";
import SettingsPage from "./pages/store/SettingsPage";
import QuizResultsPage from "./pages/store/QuizResultsPage";
import ResetPasswordPage from "./pages/store/ResetPasswordPage";
import ConversationsPage from "./pages/store/ConversationsPage";
import ConversationDetailPage from "./pages/store/ConversationDetailPage";

// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import DashboardPage from "./pages/admin/DashboardPage";
import AdminOrdersPage from "./pages/admin/OrdersPage";
import AdminOrderDetailPage from "./pages/admin/OrderDetailPage";
import AdminProductsPage from "./pages/admin/ProductsPage";
import AdminLooksPage from "./pages/admin/LooksPage";
import AdminLeadsPage from "./pages/admin/LeadsPage";
import AdminEmailTemplatesPage from "./pages/admin/EmailTemplatesPage";
import AdminPromotionsPage from "./pages/admin/PromotionsPage";
import AdminSettingsPage from "./pages/admin/SettingsPage";
import MessagesPage from "./pages/admin/MessagesPage";
import ConversationDetailPageAdmin from "./pages/admin/ConversationDetailPage";
import StaffPage from "./pages/admin/StaffPage";
import SalesPerformancePage from "./pages/admin/SalesPerformancePage";

// Protected Route Component
function ProtectedAdminRoute({ children }) {
  const { user, loading, isStaff } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center dark bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user || !isStaff()) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Store Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/looks" element={<LooksPage />} />
      <Route path="/looks/:slug" element={<LookDetailPage />} />
        <Route path="/products/:slug" element={<ProductDetailPage />} />
      <Route path="/quiz" element={<QuizPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/order/:orderId" element={<OrderConfirmationPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/account/orders" element={<OrdersPage />} />
      <Route path="/account/settings" element={<SettingsPage />} />
      <Route path="/account/quiz-results" element={<QuizResultsPage />} />
      <Route path="/account/messages" element={<ConversationsPage />} />
      <Route path="/account/messages/:conversationId" element={<ConversationDetailPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/login" element={<LoginPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedAdminRoute>
          <AdminLayout />
        </ProtectedAdminRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="orders" element={<AdminOrdersPage />} />
        <Route path="orders/:orderId" element={<AdminOrderDetailPage />} />
        <Route path="products" element={<AdminProductsPage />} />
        <Route path="looks" element={<AdminLooksPage />} />
        <Route path="leads" element={<AdminLeadsPage />} />
        <Route path="emails" element={<AdminEmailTemplatesPage />} />
        <Route path="promotions" element={<AdminPromotionsPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="messages/:conversationId" element={<ConversationDetailPageAdmin />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="sales-performance" element={<SalesPerformancePage />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    initMetaPixel();
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
